<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1>{{ $t('blacklist.new_vehicle')}}</h1>
    </div>
    <div class="details-component__content">
      <form class="form fixed-form">

        <table class="details-table tenant no-col-padding edit-table fixed-table label-col-wide-260">

          <tr class="details-table__row" :class="{'details-table__row_error' : errors.has('vehicleReg')}" v-if="isNew">
            <td class="details-table__col details-table__col_label">
              <label for="vehicleReg" class="form__label">{{ $t('parking.vehicleReg') }}</label>
            </td>
            <td class="details-table__col">
              <input type="text" class="form__input" name="vehicleReg" id="vehicleReg"
                  v-model="form.vehicleReg"
                  v-validate="'required'"
                  @keydown="stopInputAtDashAndSpace"
                  @keyup="trimVehicleReg()"/>
              <tooltip v-show="errors.has('vehicleReg')" class="form__validation">{{ $t('errors.car_field_required') }}</tooltip>
            </td>
          </tr>

          <tr v-if="isSuperAdmin()" class="details-table__row" :class="{'details-table__row_error' : errors.has('landlord')}">
            <td class="details-table__col details-table__col_label">
              <label for="landlord" class="form__label">{{ $t('common.landlord')}}</label>
            </td>
            <td class="details-table__col landlord__name">
              <el-select name="landlord"
                         v-model="landlord"
                         v-validate="'validLandlord'"
                         :data-vv-as="$t('common.landlord')"
                         v-if="isNew && landlords.items"
                         :placeholder="$t('common.landlord')"
                         @change="selectedTenant=null">
                <el-option
                  v-for="landlord in landlords.items"
                  :key="landlord.id"
                  :label="landlord.name"
                  :value="landlord.id">
                </el-option>
              </el-select>
              <span v-else-if="form.partner && form.partner.landlord.name" class="disabled">{{ form.partner.landlord.name }}</span>
              <tooltip v-show="errors.has('landlord')" class="form__validation">{{ $t('errors.landlord_field_required') }}</tooltip>
            </td>
          </tr>

          <tr class="details-table__row" v-if="isLandlord()" :class="{'details-table__row_error' : errors.has('selectedTenant')}">
            <td class="details-table__col details-table__col_label">
              <label for="tenant" class="form__label">{{ $t('common.tenant') }}</label>
            </td>
            <td class="details-table__col">
              <el-select name="selectedTenant"
                  v-model="selectedTenant"
                  :data-vv-as="$t('common.tenant')"
                  v-if="tenants.items"
                  placeholder="">
                <el-option
                  v-for="tenant in tenants.items.filter(t => t.guestParking && t.products && t.products.length > 0)"
                  v-if="tenant.id === landlord || (tenant.landlord && tenant.landlord.id === landlord)"
                  :key="tenant.id"
                  :label="tenant.name"
                  :value="tenant.id">
                </el-option>
              </el-select>
              <tooltip v-show="errors.has('selectedTenant')" class="form__validation">{{ $t('errors.tenant_field_required') }}</tooltip>
            </td>
          </tr>

        </table>

        <div class="form__buttons buttonset">
          <router-link v-if="isNew" :to="{ name: 'blacklists' }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <router-link v-else :to="{ name: 'blacklist', params: { id: $route.params.id} }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" v-on:click.prevent.stop="onSubmit()">
            {{ $t('common.save') }}
          </button>
        </div>

        <WarningModal v-if="blacklistError" @close="closeWarningModal">
          <h1 slot="header">{{ $t('common.error') }}</h1>
          <p slot="body">{{ warningBody }}</p>
        </WarningModal>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import 'styles/blocks/details.scss';
import * as mTypes from 'store/mutation-types';
import Autocomplete from 'common/Autocomplete';
import Tooltip from 'common/Tooltip';
import WarningModal from 'common/WarningModal';
import { sanitizeVehicleRegNumber } from '../../utils';

export default {
  components: {
    Autocomplete,
    Tooltip,
    WarningModal,
  },
  watch: {
    $route: 'fetchAllData',
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    ...mapGetters({
      landlords: 'landlord/landlords/landlords',
      parkingsLimit: 'landlord/blacklists/limit',
      blacklistsKeywordFilter: 'landlord/blacklists/keywordFilter',
      blacklistItem: 'blacklist/blacklist/blacklistItem',
      blacklistError: 'blacklist/blacklist/error',
      tenants: 'landlord/tenants/tenants',
    }),
  },
  data() {
    return {
      form: {},
      landlord: null,
      selectedTenant: null,
      warningBody: null,
      showWarningModal: false,
    };
  },
  mounted() {
    this.checkView();
    this.fetchAllData();
  },
  created() {
    this.checkView();
    this.resetValidators();
    this.createValidators();
  },
  methods: {
    checkView() {
      if (this.isNew) {
        this.$store.commit(`landlords/landlord/${mTypes.CLEAR_STATE}`);
      }
      this.landlord = this.user.data.selectedRole.id;
    },
    trimVehicleReg() {
      if (this.form.vehicleReg) {
        this.form.vehicleReg = sanitizeVehicleRegNumber(this.form.vehicleReg);
      }
    },
    stopInputAtDashAndSpace(event) {
      if (event.key === '-' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    fetchAllData() {
      if (this.$route.params.id) {
        this.fetchBlacklistItem();
      }
      if (this.isLandlord()) {
        this.fetchTenants();
      }
      if (this.isSuperAdmin()) {
        this.fetchLandlords();
      }
    },
    fetchBlacklistItem() {
      if (this.$route.params.id && this.user.data) {
        this.$store.dispatch('blacklist/blacklist/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
          this.form = this.blacklistItem;
          if (this.blacklistItem.partner) {
            this.selectedTenant = this.blacklistItem.partner.id;
          }
        });
      }
    },
    fetchTenants() {
      if (this.user.data) {
        this.$store.dispatch('landlord/tenants/list', { user: this.user });
      }
    },
    fetchLandlords() {
      if (this.user.data) {
        this.$store.dispatch('landlord/landlords/list', { user: this.user });
      }
    },
    onSubmit() {
      this.$validator.validateAll()
        .then((success) => {
          if (success) {
            if (this.isLandlord()) {
              this.form.partnerId = this.selectedTenant;
            }
            this.$store.dispatch('blacklist/blacklist/save', { blacklistItem: this.form, user: this.user })
              .then(() => {
                if (this.blacklistError) {
                  this.warningBody = this.$t(`errors.${this.blacklistError}`);
                  this.showWarningModal = true;
                } else {
                  this.$store.dispatch('landlord/blacklists/fetch', { user: this.user, limit: this.parkingsLimit, keyword: this.blacklistsKeywordFilter });
                  this.$router.push({ name: 'blacklist', params: { id: this.blacklistItem.id } });
                }
              });
          }
        })
        .catch(() => {});
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.$store.commit(`blacklist/blacklist/${mTypes.DATA_ERROR}`, null);
    },
    resetValidators() {
      this.$validator.remove('validLandlord');
      this.$validator.clean();
    },
    createValidators() {
      this.createLandlordValidator();
    },
    createLandlordValidator() {
      this.$validator.extend('validLandlord', {
        getMessage: () => 'Tenant must be selected',
        validate: () => this.isSuperAdmin() && (typeof this.landlord !== 'undefined' && this.landlord !== null),
      });
    },
  },
};
</script>

<style lang="scss" scoped>

.form {

  #vehicleReg {
    font-family: 'Geogrotesque Md', sans-serif !important;
    text-transform: uppercase;
  }

}
</style>




// WEBPACK FOOTER //
// src/components/blacklist/AddBlacklistView.vue